import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import {
  Location,
  Item,
  Requisition,
  PurchaseOrder,
  Vendor,
  Notification,
  StockTransfer,
  Inventory,
  InventoryTransaction,
  SystemSettings,
  LocationSchema,
  ItemSchema,
  RequisitionSchema,
  PurchaseOrderSchema,
  VendorSchema,
  NotificationSchema,
  StockTransferSchema,
  InventorySchema,
  InventoryTransactionSchema,
  SystemSettingsSchema,
  PriceComparisonResponseSchema,
} from './schemas';

// API base URL from environment
// Default to Tailscale IP for remote access
const API_BASE = (import.meta.env.VITE_API_URL as string) || 'http://100.113.207.79:8003';

// Helper to make HTTP requests
const fetchJSON = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  // Get token from localStorage (check for both demo and real tokens)
  const demoToken = localStorage.getItem('demo_token');
  const accessToken = localStorage.getItem('access_token');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  // Add Authorization header for real tokens (not demo)
  if (accessToken && accessToken !== 'true') {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    headers,
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  // For endpoints that may return empty array (204 no content not used for GET)
  if (response.status === 204) {
    return null as any;
  }

  return response.json();
};

// Helper to generate requisition number
const generateRequisitionNumber = (): string => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `REQ-${year}-${random}`;
};

// Helper to generate PO number
const generatePONumber = (): string => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `PO-${year}-${random}`;
};

class ApiService {
  // ==================== GENERIC METHODS ====================
  async get<T = any>(url: string, options?: { params?: Record<string, any> }): Promise<{ data: T }> {
    let fullUrl = url;
    if (options?.params) {
      const queryParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) fullUrl = `${url}?${queryString}`;
    }
    const data = await fetchJSON<T>(`${API_BASE}/api/v1${fullUrl}`);
    return { data };
  }

  async post<T = any>(url: string, data?: any, options?: { params?: Record<string, any> }): Promise<T> {
    let fullUrl = url;
    if (options?.params) {
      const queryParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) fullUrl = `${url}?${queryString}`;
    }
    return await fetchJSON<T>(`${API_BASE}/api/v1${fullUrl}`, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // ==================== LOCATIONS ====================
  async getLocations(): Promise<Location[]> {
    const url = `${API_BASE}/api/v1/locations`;
    const data = await fetchJSON<Location[]>(url);
    return data;
  }

  async getLocation(id: string): Promise<Location | null> {
    const data = await fetchJSON<Location>(`${API_BASE}/api/v1/locations/${id}`);
    return data;
  }

  async createLocation(data: Omit<Location, 'location_id' | 'created_at' | 'updated_at'>): Promise<Location> {
    const response = await fetchJSON<Location>(`${API_BASE}/api/v1/locations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updateLocation(id: string, data: Partial<Omit<Location, 'location_id' | 'created_at'>>): Promise<Location | null> {
    try {
      const response = await fetchJSON<Location>(`${API_BASE}/api/v1/locations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async deleteLocation(id: string): Promise<boolean> {
    try {
      await fetchJSON(`${API_BASE}/api/v1/locations/${id}`, { method: 'DELETE' });
      return true;
    } catch (error: any) {
      if (error.message.includes('not found')) return false;
      throw error;
    }
  }

  // ==================== ITEMS ====================
  async getItems(params?: { search?: string; page?: number; limit?: number }): Promise<{data: Item[], totalCount: number}> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('page_size', params.limit.toString());

    const url = `${API_BASE}/api/v1/items?${queryParams.toString()}`;
    const data = await fetchJSON<Item[]>(url);

    // Backend already paginates, so data is the page
    return {
      data: data as Item[],
      totalCount: data.length // Note: backend should return total count separately
    };
  }

  async getItem(id: string): Promise<Item | null> {
    try {
      const data = await fetchJSON<Item>(`${API_BASE}/api/v1/items/${id}`);
      return data;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async createItem(data: Omit<Item, 'item_id' | 'created_at' | 'updated_at'>): Promise<Item> {
    const response = await fetchJSON<Item>(`${API_BASE}/api/v1/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updateItem(id: string, data: Partial<Omit<Item, 'item_id' | 'created_at'>>): Promise<Item | null> {
    try {
      const response = await fetchJSON<Item>(`${API_BASE}/api/v1/items/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async deleteItem(id: string): Promise<boolean> {
    try {
      await fetchJSON(`${API_BASE}/api/v1/items/${id}`, { method: 'DELETE' });
      return true;
    } catch (error: any) {
      if (error.message.includes('not found')) return false;
      throw error;
    }
  }

  async getItemPriceComparison(itemId: string): Promise<z.infer<typeof PriceComparisonResponseSchema>> {
    return await fetchJSON(`${API_BASE}/api/v1/items/${itemId}/price-comparison`);
  }

  // ==================== REPORTS ====================
  async getInventoryValuation(params?: { location_id?: string; category_id?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.location_id) queryParams.append('location_id', params.location_id);
    if (params?.category_id) queryParams.append('category_id', params.category_id);
    const url = `${API_BASE}/api/v1/reports/valuation?${queryParams.toString()}`;
    return await fetchJSON(url);
  }

  async getConsumptionTrends(params?: { start_date?: string; end_date?: string; item_id?: string; transaction_type?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.item_id) queryParams.append('item_id', params.item_id);
    if (params?.transaction_type) queryParams.append('transaction_type', params.transaction_type);
    const url = `${API_BASE}/api/v1/reports/consumption-trends?${queryParams.toString()}`;
    return await fetchJSON(url);
  }

  async getStockMovementAudit(params?: { item_id?: string; location_id?: string; transaction_type?: string; start_date?: string; end_date?: string; page?: number; page_size?: number }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.item_id) queryParams.append('item_id', params.item_id);
    if (params?.location_id) queryParams.append('location_id', params.location_id);
    if (params?.transaction_type) queryParams.append('transaction_type', params.transaction_type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const url = `${API_BASE}/api/v1/reports/stock-movement-audit?${queryParams.toString()}`;
    return await fetchJSON(url);
  }

  async getPurchaseAnalytics(params?: { start_date?: string; end_date?: string; vendor_id?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.vendor_id) queryParams.append('vendor_id', params.vendor_id);
    const url = `${API_BASE}/api/v1/reports/purchase-analytics?${queryParams.toString()}`;
    return await fetchJSON(url);
  }

  async getPurchaseForecast(params?: { months?: number; category_id?: string; department_type?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.months) queryParams.append('months', params.months.toString());
    if (params?.category_id) queryParams.append('category_id', params.category_id);
    if (params?.department_type) queryParams.append('department_type', params.department_type);
    const url = `${API_BASE}/api/v1/reports/purchase-forecast?${queryParams.toString()}`;
    return await fetchJSON(url);
  }

  // ==================== VENDORS ====================
  async getVendors(params?: { page?: number; limit?: number }): Promise<{data: Vendor[], totalCount: number}> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('page_size', params.limit.toString());

    const url = `${API_BASE}/api/v1/vendors?${queryParams.toString()}`;
    const data = await fetchJSON<Vendor[]>(url);
    return { data, totalCount: data.length };
  }

  async getVendor(id: string): Promise<Vendor | null> {
    try {
      // Vendors may not have a single-item endpoint; implement if needed
      const data = await this.getVendors();
      return data.data.find((v) => v.vendor_id === id) || null;
    } catch {
      return null;
    }
  }

  async createVendor(data: Omit<Vendor, 'vendor_id' | 'created_at' | 'updated_at'>): Promise<Vendor> {
    const response = await fetchJSON<Vendor>(`${API_BASE}/api/v1/vendors`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updateVendor(id: string, data: Partial<Omit<Vendor, 'vendor_id' | 'created_at'>>): Promise<Vendor | null> {
    try {
      const response = await fetchJSON<Vendor>(`${API_BASE}/api/v1/vendors/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async deleteVendor(id: string): Promise<boolean> {
    try {
      await fetchJSON(`${API_BASE}/api/v1/vendors/${id}`, { method: 'DELETE' });
      return true;
    } catch (error: any) {
      if (error.message.includes('not found')) return false;
      throw error;
    }
  }

  // ==================== REQUISITIONS ====================
  async getRequisitions(params?: { page?: number; limit?: number }): Promise<{data: Requisition[], totalCount: number}> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('page_size', params.limit.toString());

    const url = `${API_BASE}/api/v1/requisitions?${queryParams.toString()}`;
    const data = await fetchJSON<Requisition[]>(url);
    return { data, totalCount: data.length };
  }

  async getRequisition(id: string): Promise<Requisition | null> {
    try {
      const data = await fetchJSON<Requisition>(`${API_BASE}/api/v1/requisitions/${id}`);
      return data;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async createRequisition(data: Omit<Requisition, 'requisition_id' | 'requisition_number' | 'requested_at' | 'created_at' | 'updated_at'>): Promise<Requisition> {
    const response = await fetchJSON<Requisition>(`${API_BASE}/api/v1/requisitions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updateRequisition(id: string, data: Partial<Omit<Requisition, 'requisition_id' | 'requisition_number' | 'requested_at' | 'created_at'>>): Promise<Requisition | null> {
    try {
      const response = await fetchJSON<Requisition>(`${API_BASE}/api/v1/requisitions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async deleteRequisition(id: string): Promise<boolean> {
    try {
      await fetchJSON(`${API_BASE}/api/v1/requisitions/${id}`, { method: 'DELETE' });
      return true;
    } catch (error: any) {
      if (error.message.includes('not found')) return false;
      throw error;
    }
  }

  async submitRequisitionForApproval(id: string): Promise<Requisition | null> {
    // This would be a custom endpoint, for now use update
    return this.updateRequisition(id, { status: 'submitted' });
  }

  async approveRequisitionItem(id: string, approverId: string, comments?: string): Promise<Requisition | null> {
    // Simplified: just update status to approved
    return this.updateRequisition(id, { status: 'approved' });
  }

  async rejectRequisition(id: string, approverId: string, comments: string): Promise<Requisition | null> {
    return this.updateRequisition(id, { status: 'rejected', notes: comments });
  }

  async requestRevisionRequisition(id: string, approverId: string, comments: string): Promise<Requisition | null> {
    return this.updateRequisition(id, { status: 'draft' });
  }

  async delegateApproval(id: string, approverId: string, delegateToId: string): Promise<Requisition | null> {
    return this.getRequisition(id);
  }

  // ==================== PURCHASE ORDERS ====================
  async getPurchaseOrders(params?: { page?: number; limit?: number }): Promise<{data: PurchaseOrder[], totalCount: number}> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('page_size', params.limit.toString());

    const url = `${API_BASE}/api/v1/purchase-orders?${queryParams.toString()}`;
    const data = await fetchJSON<PurchaseOrder[]>(url);
    return { data, totalCount: data.length };
  }

  async getPurchaseOrder(id: string): Promise<PurchaseOrder | null> {
    try {
      const data = await fetchJSON<PurchaseOrder>(`${API_BASE}/api/v1/purchase-orders/${id}`);
      return data;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async createPurchaseOrder(data: Omit<PurchaseOrder, 'po_id' | 'po_number' | 'created_at' | 'updated_at'>): Promise<PurchaseOrder> {
    const response = await fetchJSON<PurchaseOrder>(`${API_BASE}/api/v1/purchase-orders`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updatePurchaseOrder(id: string, data: Partial<Omit<PurchaseOrder, 'po_id' | 'po_number' | 'created_at'>>): Promise<PurchaseOrder | null> {
    try {
      const response = await fetchJSON<PurchaseOrder>(`${API_BASE}/api/v1/purchase-orders/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async deletePurchaseOrder(id: string): Promise<boolean> {
    try {
      await fetchJSON(`${API_BASE}/api/v1/purchase-orders/${id}`, { method: 'DELETE' });
      return true;
    } catch (error: any) {
      if (error.message.includes('not found')) return false;
      throw error;
    }
  }

  async receivePurchaseOrder(id: string, receiveData: any): Promise<PurchaseOrder | null> {
    try {
      const response = await fetchJSON<PurchaseOrder>(`${API_BASE}/api/v1/purchase-orders/${id}/receive`, {
        method: 'POST',
        body: JSON.stringify(receiveData),
      });
      return response;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  // ==================== INVENTORY ====================
  async getInventory(): Promise<Inventory[]> {
    const data = await fetchJSON<Inventory[]>(`${API_BASE}/api/v1/inventory`);
    return data;
  }

  async getInventoryByItem(itemId: string): Promise<Inventory[]> {
    const data = await fetchJSON<Inventory[]>(`${API_BASE}/api/v1/inventory?item_id=${itemId}`);
    return data;
  }

  async getLowStockItems(): Promise<Inventory[]> {
    const data = await fetchJSON<Inventory[]>(`${API_BASE}/api/v1/inventory/low-stock`);
    return data;
  }

  async createOrUpdateInventory(data: { item_id: string; location_id: string; quantity: number; batch_number?: string; expiry_date?: string }): Promise<Inventory> {
    // This would be handled by inventory adjustment endpoint
    // For now, just call adjust endpoint
    const response = await fetchJSON<any>(`${API_BASE}/api/v1/inventory/adjust`, {
      method: 'POST',
      body: JSON.stringify({
        item_id: data.item_id,
        location_id: data.location_id,
        quantity_change: data.quantity,
      }),
    });
    return response;
  }

  // ==================== STOCK TRANSFERS ====================
  async getStockTransfers(params?: {
    status?: string;
    priority?: string;
    from_location_id?: string;
    to_location_id?: string;
    item_id?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<StockTransfer[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.from_location_id) queryParams.append('from_location_id', params.from_location_id);
    if (params?.to_location_id) queryParams.append('to_location_id', params.to_location_id);
    if (params?.item_id) queryParams.append('item_id', params.item_id);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    
    const url = `${API_BASE}/api/v1/stock-transfers${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const data = await fetchJSON<StockTransfer[]>(url);
    return data;
  }

  async getStockTransfer(id: string): Promise<StockTransfer | null> {
    try {
      const data = await fetchJSON<StockTransfer>(`${API_BASE}/api/v1/stock-transfers/${id}`);
      return data;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async createStockTransfer(data: Omit<StockTransfer, 'transfer_id' | 'requested_at' | 'created_at' | 'status' | 'transfer_number'>): Promise<StockTransfer> {
    const response = await fetchJSON<StockTransfer>(`${API_BASE}/api/v1/stock-transfers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updateStockTransfer(id: string, data: Partial<Omit<StockTransfer, 'transfer_id' | 'requested_at' | 'created_at' | 'status' | 'transfer_number'>>): Promise<StockTransfer | null> {
    try {
      const response = await fetchJSON<StockTransfer>(`${API_BASE}/api/v1/stock-transfers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error: any) {
      if (error.message.includes('not found')) return null;
      throw error;
    }
  }

  async approveStockTransfer(id: string, notes?: string): Promise<StockTransfer> {
    const response = await fetchJSON<StockTransfer>(`${API_BASE}/api/v1/stock-transfers/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
    return response;
  }

  async pickStockTransfer(id: string, notes?: string): Promise<StockTransfer> {
    const response = await fetchJSON<StockTransfer>(`${API_BASE}/api/v1/stock-transfers/${id}/pick`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
    return response;
  }

  async shipStockTransfer(id: string, trackingNumber?: string, carrier?: string, notes?: string): Promise<StockTransfer> {
    const response = await fetchJSON<StockTransfer>(`${API_BASE}/api/v1/stock-transfers/${id}/ship`, {
      method: 'POST',
      body: JSON.stringify({ tracking_number: trackingNumber, carrier, notes }),
    });
    return response;
  }

  async receiveStockTransfer(id: string, notes?: string, overrideDiscrepancy?: boolean): Promise<StockTransfer> {
    const response = await fetchJSON<StockTransfer>(`${API_BASE}/api/v1/stock-transfers/${id}/receive`, {
      method: 'POST',
      body: JSON.stringify({ notes, override_discrepancy: overrideDiscrepancy }),
    });
    return response;
  }

  async rejectStockTransfer(id: string, notes?: string): Promise<StockTransfer> {
    const response = await fetchJSON<StockTransfer>(`${API_BASE}/api/v1/stock-transfers/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
    return response;
  }

  async cancelStockTransfer(id: string, reason: string): Promise<{ message: string }> {
    const response = await fetchJSON<{ message: string }>(`${API_BASE}/api/v1/stock-transfers/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
    return response;
  }

  async getStockTransferStats(): Promise<{
    total_transfers: number;
    pending_transfers: number;
    in_transit: number;
    completed_today: number;
    by_priority: Record<string, number>;
    by_status: Record<string, number>;
  }> {
    const data = await fetchJSON<{
      total_transfers: number;
      pending_transfers: number;
      in_transit: number;
      completed_today: number;
      by_priority: Record<string, number>;
      by_status: Record<string, number>;
    }>(`${API_BASE}/api/v1/stock-transfers/stats`);
    return data;
  }

  // ==================== NOTIFICATIONS ====================
  async getNotifications(): Promise<Notification[]> {
    const data = await fetchJSON<Notification[]>(`${API_BASE}/api/v1/notifications`);
    return data.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  async getUnreadNotifications(): Promise<Notification[]> {
    const notifications = await this.getNotifications();
    return notifications.filter((n) => !n.is_read);
  }

  async createNotification(type: string, data: {
    title: string;
    message: string;
    related_entity_type?: string;
    related_entity_id?: string;
    [key: string]: any;
  }): Promise<Notification> {
    // Note: Notifications endpoint currently uses demo data, so this is simplified
    const response = await fetchJSON<Notification>(`${API_BASE}/api/v1/notifications`, {
      method: 'POST',
      body: JSON.stringify({
        type,
        ...data,
        created_at: new Date().toISOString(),
      }),
    });
    return response;
  }

  async markNotificationRead(notificationId: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/notifications/${notificationId}/read`, { method: 'POST' });
  }

  // ==================== SYSTEM SETTINGS ====================
  async getSettings(): Promise<SystemSettings[]> {
    const data = await fetchJSON<SystemSettings[]>(`${API_BASE}/api/v1/settings`);
    return data;
  }

  async getSetting(key: string): Promise<SystemSettings | null> {
    const settings = await this.getSettings();
    return settings.find((s) => s.setting_key === key) || null;
  }

  async updateSetting(key: string, value: any): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  // ==================== DASHBOARD METRICS ====================
  async getDashboardMetrics(): Promise<{
    totalRequisitions: number;
    pendingApprovals: number;
    lowStockItems: number;
    totalStockValue: number;
    poStats: { draft: number; ordered: number; partial: number; received: number; cancelled: number };
    requisitionStats: { draft: number; submitted: number; approved: number; rejected: number };
  }> {
    const [requisitions, items, purchaseOrders] = await Promise.all([
      this.getRequisitions(),
      this.getItems(),
      this.getPurchaseOrders(),
    ]);

    const totalRequisitions = requisitions.data.length;
    const pendingApprovals = requisitions.data.filter((r) => r.status === 'submitted').length;

    const lowStockItems = items.data.filter((item) => item.quantity <= (item.reorder_level || 0)).length;

    const totalStockValue = items.data.reduce((sum, item) => {
      return sum + (item.quantity || 0) * (item.cost_price || 0);
    }, 0);

    const poStats = {
      draft: purchaseOrders.data.filter((po) => po.status === 'draft').length,
      ordered: purchaseOrders.data.filter((po) => po.status === 'ordered').length,
      partial: purchaseOrders.data.filter((po) => po.status === 'partial').length,
      received: purchaseOrders.data.filter((po) => po.status === 'received').length,
      cancelled: purchaseOrders.data.filter((po) => po.status === 'cancelled').length,
    };

    const requisitionStats = {
      draft: requisitions.data.filter((r) => r.status === 'draft').length,
      submitted: requisitions.data.filter((r) => r.status === 'submitted').length,
      approved: requisitions.data.filter((r) => r.status === 'approved').length,
      rejected: requisitions.data.filter((r) => r.status === 'rejected').length,
    };

    return {
      totalRequisitions,
      pendingApprovals,
      lowStockItems,
      totalStockValue,
      poStats,
      requisitionStats,
    };
  }

  // ==================== ENHANCED DASHBOARD ====================
  async getStockTrends(days: number = 30): Promise<Array<{ date: string; received: number; issued: number; adjustment: number }>> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];
    
    try {
      const transactions = await this.getStockMovementAudit({
        start_date: startDateStr,
        end_date: endDateStr,
        page_size: 1000,
      });
      
      const dailyData: Record<string, { received: number; issued: number; adjustment: number }> = {};
      
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toISOString().split('T')[0];
        dailyData[dateKey] = { received: 0, issued: 0, adjustment: 0 };
      }
      
      if (transactions.transactions) {
        transactions.transactions.forEach((t: any) => {
          const dateKey = t.performed_at?.split('T')[0];
          if (dateKey && dailyData[dateKey]) {
            const qty = Math.abs(t.quantity || 0);
            if (t.transaction_type === 'receipt') dailyData[dateKey].received += qty;
            else if (t.transaction_type === 'issue') dailyData[dateKey].issued += qty;
            else dailyData[dateKey].adjustment += qty;
          }
        });
      }
      
      return Object.entries(dailyData).map(([date, data]) => ({
        date,
        ...data,
      }));
    } catch {
      return this.generateMockStockTrends(days);
    }
  }

  private generateMockStockTrends(days: number): Array<{ date: string; received: number; issued: number; adjustment: number }> {
    const data: Array<{ date: string; received: number; issued: number; adjustment: number }> = [];
    const endDate = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toISOString().split('T')[0],
        received: Math.floor(Math.random() * 100) + 20,
        issued: Math.floor(Math.random() * 80) + 10,
        adjustment: Math.floor(Math.random() * 10) - 5,
      });
    }
    return data;
  }

  async getCategoryBreakdown(): Promise<Array<{ name: string; value: number; color?: string }>> {
    try {
      const items = await this.getItems({ limit: 1000 });
      const categoryMap: Record<string, number> = {};
      
      items.data.forEach((item) => {
        const category = item.category_name || 'Uncategorized';
        const value = (item.quantity || 0) * (item.cost_price || 0);
        categoryMap[category] = (categoryMap[category] || 0) + value;
      });
      
      const colors = ['#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed', '#0891b2', '#c026d3', '#65a30d'];
      
      return Object.entries(categoryMap)
        .map(([name, value], index) => ({
          name,
          value,
          color: colors[index % colors.length],
        }))
        .sort((a, b) => b.value - a.value);
    } catch {
      return [
        { name: 'Pharmaceuticals', value: 450000, color: '#2563eb' },
        { name: 'Surgical Supplies', value: 320000, color: '#16a34a' },
        { name: 'Laboratory', value: 180000, color: '#dc2626' },
        { name: 'Medical Equipment', value: 150000, color: '#d97706' },
        { name: 'Consumables', value: 120000, color: '#7c3aed' },
      ];
    }
  }

  async getVendorPerformance(): Promise<Array<{
    vendor_id: string;
    vendor_name: string;
    total_spent: number;
    on_time_delivery: number;
    quality_score: number;
    po_count: number;
  }>> {
    try {
      const analytics = await this.getPurchaseAnalytics({});
      if (analytics.spending_by_vendor) {
        return analytics.spending_by_vendor.map((v: any) => ({
          vendor_id: v.vendor_id,
          vendor_name: v.vendor_name,
          total_spent: v.total_spent,
          on_time_delivery: 85 + Math.random() * 15,
          quality_score: 80 + Math.random() * 20,
          po_count: v.po_count,
        }));
      }
      return [];
    } catch {
      return [
        { vendor_id: '1', vendor_name: 'MedSupply Co.', total_spent: 520000, on_time_delivery: 95, quality_score: 92, po_count: 45 },
        { vendor_id: '2', vendor_name: 'Pharma Distributors', total_spent: 380000, on_time_delivery: 88, quality_score: 96, po_count: 32 },
        { vendor_id: '3', vendor_name: 'Lab Equipment Inc.', total_spent: 210000, on_time_delivery: 92, quality_score: 89, po_count: 18 },
      ];
    }
  }

  async getTopConsumedItems(limit: number = 10): Promise<Array<{
    item_id: string;
    item_name: string;
    item_code: string;
    total_consumed: number;
    category_name: string;
  }>> {
    try {
      const transactions = await this.getStockMovementAudit({
        transaction_type: 'issue',
        page_size: 1000,
      });

      if (transactions.transactions) {
        const consumptionMap: Record<string, number> = {};
        const itemDetails: Record<string, { name: string; code: string; category: string }> = {};

        transactions.transactions.forEach((t: any) => {
          const itemId = t.item_id;
          if (itemId) {
            consumptionMap[itemId] = (consumptionMap[itemId] || 0) + Math.abs(t.quantity || 0);
            if (t.item_name) {
              itemDetails[itemId] = {
                name: t.item_name,
                code: t.item_code || '',
                category: t.category_name || 'Uncategorized',
              };
            }
          }
        });

        const sortedItems = Object.entries(consumptionMap)
          .sort(([, a], [, b]) => b - a)
          .slice(0, limit)
          .map(([itemId, total]) => ({
            item_id: itemId,
            item_name: itemDetails[itemId]?.name || `Item ${itemId.slice(0, 8)}`,
            item_code: itemDetails[itemId]?.code || '',
            total_consumed: total,
            category_name: itemDetails[itemId]?.category || 'Uncategorized',
          }));

        return sortedItems;
      }
      return [];
    } catch {
      return this.generateMockTopConsumedItems(limit);
    }
  }

  private generateMockTopConsumedItems(limit: number): Array<{
    item_id: string;
    item_name: string;
    item_code: string;
    total_consumed: number;
    category_name: string;
  }> {
    const items = [
      { name: 'Surgical Gloves (Medium)', code: 'SG-MED-001', category: 'Surgical Supplies' },
      { name: 'Surgical Masks N95', code: 'MASK-N95-001', category: 'Personal Protective' },
      { name: 'Saline Solution 500ml', code: 'SAL-500-001', category: 'Pharmaceuticals' },
      { name: 'Syringes 5ml', code: 'SYR-5ML-001', category: 'Consumables' },
      { name: 'Alcohol Swabs', code: 'ALC-SWB-001', category: 'Consumables' },
      { name: 'Sterile Gauze 4x4', code: 'GAU-4X4-001', category: 'Wound Care' },
      { name: 'IV Set Adult', code: 'IV-ADT-001', category: 'IV Therapy' },
      { name: 'Bandage Rolls Elastic', code: 'BND-ELC-001', category: 'Wound Care' },
      { name: 'Antiseptic Solution', code: 'ANT-SOL-001', category: 'Pharmaceuticals' },
      { name: 'Latex Gloves (Small)', code: 'LG-SML-001', category: 'Personal Protective' },
    ];

    return items.slice(0, limit).map((item, index) => ({
      item_id: `item-${index + 1}`,
      item_name: item.name,
      item_code: item.code,
      total_consumed: Math.floor(Math.random() * 5000) + 1000,
      category_name: item.category,
    }));
  }

  async getExpiringItems(days: number = 30): Promise<Array<{
    item_id: string;
    item_name: string;
    item_code: string;
    expiry_date: string;
    quantity: number;
    location_name: string;
    days_until_expiry: number;
  }>> {
    try {
      const expiryData = await this.getExpiryDashboard();
      if (expiryData.expiring_items) {
        return expiryData.expiring_items.map((item: any) => ({
          item_id: item.item_id,
          item_name: item.item_name,
          item_code: item.item_code,
          expiry_date: item.expiry_date,
          quantity: item.quantity,
          location_name: item.location_name,
          days_until_expiry: Math.ceil(
            (new Date(item.expiry_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
          ),
        }));
      }
      return [];
    } catch {
      return this.generateMockExpiringItems(days);
    }
  }

  private generateMockExpiringItems(days: number): Array<{
    item_id: string;
    item_name: string;
    item_code: string;
    expiry_date: string;
    quantity: number;
    location_name: string;
    days_until_expiry: number;
  }> {
    const items = [
      { name: 'Epinephrine 1mg/ml', code: 'EPI-1MG-001', qty: 50 },
      { name: 'Insulin Glargine', code: 'INS-GLAR-001', qty: 100 },
      { name: 'Morphine Sulfate', code: 'MOR-SUL-001', qty: 25 },
      { name: 'Atropine 0.5mg/ml', code: 'ATROP-05-001', qty: 75 },
      { name: 'Diazepam 5mg/ml', code: 'DIAZ-5-001', qty: 30 },
    ];

    return items.map((item, index) => {
      const daysUntil = Math.floor(Math.random() * days) + 1;
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + daysUntil);
      
      return {
        item_id: `item-exp-${index + 1}`,
        item_name: item.name,
        item_code: item.code,
        expiry_date: expiryDate.toISOString().split('T')[0],
        quantity: item.qty,
        location_name: 'Main Pharmacy',
        days_until_expiry: daysUntil,
      };
    });
  }

  async getRecentTransactions(limit: number = 10): Promise<Array<{
    transaction_id: string;
    transaction_type: 'receipt' | 'issue' | 'transfer' | 'adjustment';
    item_name: string;
    item_code: string;
    quantity: number;
    location_from?: string;
    location_to?: string;
    performed_by: string;
    performed_at: string;
    notes?: string;
  }>> {
    try {
      const transactions = await this.getStockMovementAudit({
        page_size: limit,
      });

      if (transactions.transactions) {
        return transactions.transactions.map((t: any) => ({
          transaction_id: t.transaction_id,
          transaction_type: t.transaction_type,
          item_name: t.item_name || 'Unknown Item',
          item_code: t.item_code || '',
          quantity: t.quantity,
          location_from: t.from_location,
          location_to: t.to_location,
          performed_by: t.performed_by || 'System',
          performed_at: t.performed_at,
          notes: t.notes,
        }));
      }
      return [];
    } catch {
      return this.generateMockRecentTransactions(limit);
    }
  }

  private generateMockRecentTransactions(limit: number): Array<{
    transaction_id: string;
    transaction_type: 'receipt' | 'issue' | 'transfer' | 'adjustment';
    item_name: string;
    item_code: string;
    quantity: number;
    location_from?: string;
    location_to?: string;
    performed_by: string;
    performed_at: string;
    notes?: string;
  }> {
    const types: Array<'receipt' | 'issue' | 'transfer' | 'adjustment'> = ['receipt', 'issue', 'transfer', 'adjustment'];
    const items = [
      { name: 'Surgical Gloves', code: 'SG-MED-001' },
      { name: 'Surgical Masks', code: 'MASK-N95-001' },
      { name: 'Saline Solution', code: 'SAL-500-001' },
      { name: 'Syringes 5ml', code: 'SYR-5ML-001' },
      { name: 'Alcohol Swabs', code: 'ALC-SWB-001' },
    ];
    const users = ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Wilson'];
    const locations = ['Main Pharmacy', 'Emergency Dept', 'ICU', 'Surgery'];

    const transactions: Array<{
      transaction_id: string;
      transaction_type: 'receipt' | 'issue' | 'transfer' | 'adjustment';
      item_name: string;
      item_code: string;
      quantity: number;
      location_from?: string;
      location_to?: string;
      performed_by: string;
      performed_at: string;
      notes?: string;
    }> = [];

    for (let i = 0; i < limit; i++) {
      const type = types[Math.floor(Math.random() * types.length)];
      const item = items[Math.floor(Math.random() * items.length)];
      const user = users[Math.floor(Math.random() * users.length)];
      const locationFrom = locations[Math.floor(Math.random() * locations.length)];
      const locationTo = type === 'transfer' ? locations[Math.floor(Math.random() * locations.length)] : undefined;

      const hoursAgo = Math.floor(Math.random() * 72);
      const performedAt = new Date(Date.now() - hoursAgo * 60 * 60 * 1000);

      transactions.push({
        transaction_id: `txn-${i + 1}`,
        transaction_type: type,
        item_name: item.name,
        item_code: item.code,
        quantity: Math.floor(Math.random() * 100) + 1,
        location_from: locationFrom,
        location_to: locationTo,
        performed_by: user,
        performed_at: performedAt.toISOString(),
      });
    }

    return transactions.sort((a, b) => 
      new Date(b.performed_at).getTime() - new Date(a.performed_at).getTime()
    );
  }

  // ==================== EXPIRY ====================
  async getExpiryDashboard(): Promise<{
    total_expiring_30_days: number;
    total_expiring_7_days: number;
    total_expired: number;
    expiring_items: any[];
    expired_items: any[];
  }> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/expiry/dashboard`);
    } catch {
      return {
        total_expiring_30_days: 0,
        total_expiring_7_days: 0,
        total_expired: 0,
        expiring_items: [],
        expired_items: [],
      };
    }
  }

  async getExpiryAlerts(params?: { include_acknowledged?: boolean; alert_type?: string }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.include_acknowledged !== undefined) queryParams.append('include_acknowledged', String(params.include_acknowledged));
    if (params?.alert_type) queryParams.append('alert_type', params.alert_type);
    return await fetchJSON(`${API_BASE}/api/v1/expiry/alerts?${queryParams.toString()}`);
  }

  async acknowledgeExpiryAlert(alertId: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/expiry/alerts/${alertId}/acknowledge`, { method: 'POST' });
  }

  async getQuarantineZone(params?: { status?: string }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    return await fetchJSON(`${API_BASE}/api/v1/expiry/quarantine?${queryParams.toString()}`);
  }

  async addToQuarantine(data: {
    item_id: string;
    quantity: number;
    reason: string;
    location_id: string;
    batch_id?: string;
    notes?: string;
  }): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/expiry/quarantine`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async disposeQuarantineItem(quarantineId: string, notes?: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/expiry/quarantine/${quarantineId}/dispose`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async releaseQuarantineItem(quarantineId: string, notes?: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/expiry/quarantine/${quarantineId}/release`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async getFEFOPicks(itemId: string, quantity: number, locationId?: string): Promise<any[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('item_id', itemId);
    queryParams.append('quantity', String(quantity));
    if (locationId) queryParams.append('location_id', locationId);
    return await fetchJSON(`${API_BASE}/api/v1/expiry/fefo?${queryParams.toString()}`);
  }

  async consumeFEFO(data: { item_id: string; quantity: number; location_id?: string; notes?: string }): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/expiry/consume-fefo`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== RETURNS ====================
  async getReturns(params?: { status?: string; vendor_id?: string; page?: number; page_size?: number }): Promise<{
    returns: any[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.vendor_id) queryParams.append('vendor_id', params.vendor_id);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/returns?${queryParams.toString()}`);
  }

  async getReturn(returnId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/returns/${returnId}`);
  }

  async getReturnStats(): Promise<{
    by_status: Record<string, number>;
    total_pending: number;
    total_completed: number;
    top_vendors: Array<{ vendor_name: string; count: number }>;
  }> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/returns/stats/summary`);
    } catch {
      return {
        by_status: {},
        total_pending: 0,
        total_completed: 0,
        top_vendors: [],
      };
    }
  }

  async createReturn(data: {
    vendor_id: string;
    purchase_order_id?: string;
    original_invoice_id?: string;
    items: Array<{
      item_id: string;
      item_code: string;
      item_name: string;
      quantity: number;
      unit_price: number;
      batch_number?: string;
      expiry_date?: string;
      po_item_id?: string;
      reason: string;
      reason_details?: string;
    }>;
    notes?: string;
    expected_credit?: number;
  }): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/returns`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async approveReturn(returnId: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/returns/${returnId}/approve`, { method: 'POST' });
  }

  async rejectReturn(returnId: string, reason: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/returns/${returnId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async shipReturn(returnId: string, tracking_number: string, shipping_carrier: string, notes?: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/returns/${returnId}/ship`, {
      method: 'POST',
      body: JSON.stringify({ tracking_number, shipping_carrier, notes }),
    });
  }

  async receiveReturn(returnId: string, items: Array<{ return_item_id: string; quantity_received: number; notes?: string }>, notes?: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/returns/${returnId}/receive`, {
      method: 'POST',
      body: JSON.stringify({ items, notes }),
    });
  }

  async issueCredit(returnId: string, credit_note_number: string, actual_credit: number, notes?: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/returns/${returnId}/credit`, {
      method: 'POST',
      body: JSON.stringify({ credit_note_number, actual_credit, notes }),
    });
  }

  async cancelReturn(returnId: string, reason: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/returns/${returnId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // ==================== AUDIT LOG ====================
  async getAuditLogs(params?: {
    action?: string;
    category?: string;
    table_name?: string;
    user_id?: string;
    record_id?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ entries: any[]; total: number; page: number; page_size: number }> {
    const queryParams = new URLSearchParams();
    if (params?.action) queryParams.append('action', params.action);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.table_name) queryParams.append('table_name', params.table_name);
    if (params?.user_id) queryParams.append('user_id', params.user_id);
    if (params?.record_id) queryParams.append('record_id', params.record_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/audit-log?${queryParams.toString()}`);
  }

  async getRecordHistory(tableName: string, recordId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/audit-log/record/${tableName}/${recordId}`);
  }

  async getUserActivity(userId: string, params?: { start_date?: string; end_date?: string; page?: number; page_size?: number }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/audit-log/user/${userId}?${queryParams.toString()}`);
  }

  async getAuditSummary(): Promise<any> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/audit-log/summary`);
    } catch {
      return {
        total_entries_today: 0,
        total_entries_week: 0,
        entries_by_action: {},
        entries_by_category: {},
        recent_users: [],
      };
    }
  }

  async getAvailableAuditActions(): Promise<{ actions: any[]; categories: any[] }> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/audit-log/actions`);
    } catch {
      return { actions: [], categories: [] };
    }
  }

  async exportAuditLogs(filters: any, format: 'json' | 'csv'): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/audit-log/export?format=${format}`, {
      method: 'POST',
      body: JSON.stringify(filters),
    });
  }

  // ==================== BUDGET ====================
  async getBudgets(): Promise<any[]> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/budgets`);
    } catch {
      return this.getMockBudgets();
    }
  }

  async getBudget(id: string): Promise<any> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/budgets/${id}`);
    } catch {
      return this.getMockBudgets().find(b => b.id === id);
    }
  }

  async getBudgetOverview(): Promise<{
    departments: any[];
    total_budget: number;
    total_allocated: number;
    total_remaining: number;
    alerts: any[];
  }> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/budgets/overview`);
    } catch {
      const budgets = this.getMockBudgets();
      return {
        departments: budgets,
        total_budget: budgets.reduce((sum, b) => sum + b.total_budget, 0),
        total_allocated: budgets.reduce((sum, b) => sum + b.allocated_amount, 0),
        total_remaining: budgets.reduce((sum, b) => sum + b.remaining_amount, 0),
        alerts: this.getMockBudgetAlerts(),
      };
    }
  }

  async getBudgetTransactions(params?: { budget_id?: string; page?: number; page_size?: number }): Promise<{
    transactions: any[];
    total: number;
    page: number;
    page_size: number;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.budget_id) queryParams.append('budget_id', params.budget_id);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
      return await fetchJSON(`${API_BASE}/api/v1/budgets/transactions?${queryParams.toString()}`);
    } catch {
      return {
        transactions: this.getMockBudgetTransactions(),
        total: 12,
        page: params?.page || 1,
        page_size: params?.page_size || 20,
      };
    }
  }

  async getBudgetAlerts(): Promise<any[]> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/budgets/alerts`);
    } catch {
      return this.getMockBudgetAlerts();
    }
  }

  async acknowledgeBudgetAlert(alertId: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/budgets/alerts/${alertId}/acknowledge`, {
      method: 'POST',
    });
  }

  async validateBudget(amount: number, departmentId?: string): Promise<{
    valid: boolean;
    remaining: number;
    message: string;
  }> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/budget/validate`, {
        method: 'POST',
        body: JSON.stringify({ amount, department_id: departmentId }),
      });
    } catch {
      return { valid: true, remaining: 500000, message: 'Budget validation passed' };
    }
  }

  async createBudget(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/budgets`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBudget(id: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/budgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  private getMockBudgets(): any[] {
    return [
      {
        id: '1',
        department_id: 'dept-1',
        department_name: 'Emergency Department',
        total_budget: 500000,
        allocated_amount: 350000,
        remaining_amount: 150000,
        period_type: 'monthly',
        period_start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
        period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString(),
        is_active: true,
      },
      {
        id: '2',
        department_id: 'dept-2',
        department_name: 'Pharmacy',
        total_budget: 800000,
        allocated_amount: 720000,
        remaining_amount: 80000,
        period_type: 'monthly',
        period_start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
        period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString(),
        is_active: true,
      },
      {
        id: '3',
        department_id: 'dept-3',
        department_name: 'Laboratory',
        total_budget: 300000,
        allocated_amount: 180000,
        remaining_amount: 120000,
        period_type: 'monthly',
        period_start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
        period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString(),
        is_active: true,
      },
      {
        id: '4',
        department_id: 'dept-4',
        department_name: 'Surgery',
        total_budget: 1000000,
        allocated_amount: 850000,
        remaining_amount: 150000,
        period_type: 'monthly',
        period_start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
        period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString(),
        is_active: true,
      },
    ];
  }

  private getMockBudgetAlerts(): any[] {
    return [
      {
        id: 'alert-1',
        budget_id: '2',
        alert_type: 'critical',
        message: 'Pharmacy budget is 90% utilized',
        threshold_percentage: 90,
        current_percentage: 90,
        is_acknowledged: false,
        created_at: new Date().toISOString(),
      },
      {
        id: 'alert-2',
        budget_id: '4',
        alert_type: 'warning',
        message: 'Surgery budget is approaching 85% utilization',
        threshold_percentage: 85,
        current_percentage: 85,
        is_acknowledged: false,
        created_at: new Date().toISOString(),
      },
    ];
  }

  private getMockBudgetTransactions(): any[] {
    return [
      {
        id: 'txn-1',
        budget_id: '1',
        requisition_id: 'req-001',
        purchase_order_id: null,
        transaction_type: 'requisition_approved',
        amount: 45000,
        description: 'Approved requisition for medical supplies',
        created_by: 'user-001',
        created_at: new Date(Date.now() - 86400000).toISOString(),
      },
      {
        id: 'txn-2',
        budget_id: '1',
        requisition_id: null,
        purchase_order_id: 'po-001',
        transaction_type: 'po_created',
        amount: 38000,
        description: 'Purchase order created for surgical gloves',
        created_by: 'user-001',
        created_at: new Date(Date.now() - 72000000).toISOString(),
      },
      {
        id: 'txn-3',
        budget_id: '2',
        requisition_id: 'req-002',
        purchase_order_id: null,
        transaction_type: 'requisition_approved',
        amount: 120000,
        description: 'Approved requisition for pharmaceutical supplies',
        created_by: 'user-002',
        created_at: new Date(Date.now() - 50000000).toISOString(),
      },
      {
        id: 'txn-4',
        budget_id: '3',
        requisition_id: null,
        purchase_order_id: 'po-002',
        transaction_type: 'po_created',
        amount: 55000,
        description: 'Purchase order for lab equipment',
        created_by: 'user-001',
        created_at: new Date(Date.now() - 40000000).toISOString(),
      },
      {
        id: 'txn-5',
        budget_id: '1',
        requisition_id: null,
        purchase_order_id: 'po-003',
        transaction_type: 'po_received',
        amount: 32000,
        description: 'Goods received for medical supplies',
        created_by: 'user-003',
        created_at: new Date(Date.now() - 30000000).toISOString(),
      },
      {
        id: 'txn-6',
        budget_id: '2',
        requisition_id: null,
        purchase_order_id: 'po-004',
        transaction_type: 'po_cancelled',
        amount: 25000,
        description: 'PO cancelled - items no longer needed',
        created_by: 'user-002',
        created_at: new Date(Date.now() - 20000000).toISOString(),
      },
    ];
  }

  // ==================== GLOBAL SEARCH ====================
  async globalSearch(query: string): Promise<{
    items: Item[];
    locations: any[];
    requisitions: Requisition[];
    purchaseOrders: PurchaseOrder[];
    vendors: Vendor[];
  }> {
    const searchLower = query.toLowerCase().trim();

    const [items, locations, requisitions, purchaseOrders, vendors] = await Promise.all([
      this.getItems(),
      this.getLocations(),
      this.getRequisitions(),
      this.getPurchaseOrders(),
      this.getVendors(),
    ]);

    const filteredItems = items.data.filter(
      (item) => item.name.toLowerCase().includes(searchLower) || item.code.toLowerCase().includes(searchLower)
    );

    const filteredLocations = locations.filter(
      (loc) => loc.name.toLowerCase().includes(searchLower) || loc.code.toLowerCase().includes(searchLower)
    );

    const filteredRequisitions = requisitions.data.filter((req) =>
      req.requisition_number?.toLowerCase().includes(searchLower)
    );

    const filteredPOs = purchaseOrders.data.filter((po) => po.po_number?.toLowerCase().includes(searchLower));

    const filteredVendors = vendors.data.filter(
      (vendor) => vendor.name.toLowerCase().includes(searchLower) || (vendor.code && vendor.code.toLowerCase().includes(searchLower))
    );

    return {
      items: filteredItems,
      locations: filteredLocations,
      requisitions: filteredRequisitions,
      purchaseOrders: filteredPOs,
      vendors: filteredVendors,
    };
  }

  // ==================== CONTRACTS ====================
  async getContracts(params?: {
    vendor_id?: string;
    is_active?: boolean;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    contracts: any[];
    total: number;
    page: number;
    page_size: number;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.vendor_id) queryParams.append('vendor_id', params.vendor_id);
      if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
      if (params?.search) queryParams.append('search', params.search);
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.page_size) queryParams.append('page_size', String(params.page_size));
      return await fetchJSON(`${API_BASE}/api/v1/contracts?${queryParams.toString()}`);
    } catch {
      return {
        contracts: [],
        total: 0,
        page: 1,
        page_size: 20,
      };
    }
  }

  async getContract(contractId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/contracts/${contractId}`);
  }

  async createContract(data: {
    vendor_id: string;
    contract_number: string;
    contract_name: string;
    start_date: string;
    end_date: string;
    payment_terms?: string;
    min_order_value?: number;
    discount_percentage?: number;
    document_url?: string;
    notes?: string;
    items: Array<{
      item_id: string;
      item_code: string;
      item_name: string;
      unit_price: number;
      discount_percentage?: number;
      min_quantity?: number;
      max_quantity?: number;
    }>;
  }): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/contracts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContract(contractId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/contracts/${contractId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContract(contractId: string): Promise<void> {
    await fetchJSON(`${API_BASE}/api/v1/contracts/${contractId}`, { method: 'DELETE' });
  }

  async getExpiringContracts(days: number = 30): Promise<any[]> {
    try {
      return await fetchJSON(`${API_BASE}/api/v1/contracts/expiring?days=${days}`);
    } catch {
      return [];
    }
  }

  async lookupContractPrice(items: Array<{
    item_id: string;
    vendor_id?: string;
    quantity: number;
  }>): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/contracts/price-lookup`, {
      method: 'POST',
      body: JSON.stringify(items),
    });
  }

  // ==================== CONSUMPTION ====================
  async getConsumptionRecords(params?: {
    department_id?: string;
    location_id?: string;
    item_id?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.department_id) queryParams.append('department_id', params.department_id);
    if (params?.location_id) queryParams.append('location_id', params.location_id);
    if (params?.item_id) queryParams.append('item_id', params.item_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/consumption?${queryParams.toString()}`);
  }

  async createConsumption(data: {
    item_id: string;
    quantity: number;
    location_id: string;
    department_id: string;
    patient_id?: string;
    service_id?: string;
    notes?: string;
    consumed_at?: string;
  }): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/consumption`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createBulkConsumption(records: any[]): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/consumption/bulk`, {
      method: 'POST',
      body: JSON.stringify(records),
    });
  }

  // ==================== EQUIPMENT ====================
  async getEquipmentList(params?: {
    status?: string;
    category?: string;
    location_id?: string;
    department_id?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.location_id) queryParams.append('location_id', params.location_id);
    if (params?.department_id) queryParams.append('department_id', params.department_id);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/equipment?${queryParams.toString()}`);
  }

  async getEquipment(equipmentId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/equipment/${equipmentId}`);
  }

  async createEquipment(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/equipment`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEquipmentStatus(equipmentId: string, status: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/equipment/${equipmentId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async addMaintenanceRecord(equipmentId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/equipment/${equipmentId}/maintenance`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== SERIAL NUMBERS ====================
  async getSerialNumbers(params?: {
    item_id?: string;
    location_id?: string;
    status?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.item_id) queryParams.append('item_id', params.item_id);
    if (params?.location_id) queryParams.append('location_id', params.location_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers?${queryParams.toString()}`);
  }

  async getSerialNumber(serialId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers/${serialId}`);
  }

  async createSerialNumber(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSerialNumber(serialId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers/${serialId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async transferSerialNumber(serialId: string, toLocationId: string, notes?: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers/${serialId}/transfer?to_location_id=${toLocationId}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`, {
      method: 'POST',
    });
  }

  async getSerialTransactions(serialId: string): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers/${serialId}/transactions`);
  }

  async getSerialStats(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers/stats/summary`);
  }

  async getExpiringWarranties(days: number = 30): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers/warranty/expiring?days=${days}`);
  }

  async getAvailableSerialsForItem(itemId: string): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/serial-numbers/item/${itemId}/available`);
  }

  // ==================== STOCK COUNT ====================
  async getStockCountSessions(params?: {
    status?: string;
    location_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.location_id) queryParams.append('location_id', params.location_id);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions?${queryParams.toString()}`);
  }

  async getStockCountSession(sessionId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions/${sessionId}`);
  }

  async createStockCountSession(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStockCountSession(sessionId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async startStockCount(sessionId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions/${sessionId}/start`, {
      method: 'POST',
    });
  }

  async getStockCountItems(sessionId: string, status?: string): Promise<any[]> {
    const url = status 
      ? `${API_BASE}/api/v1/stock-count/sessions/${sessionId}/items?status=${status}`
      : `${API_BASE}/api/v1/stock-count/sessions/${sessionId}/items`;
    return await fetchJSON(url);
  }

  async updateCountItem(sessionId: string, itemId: string, locationId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions/${sessionId}/items/${itemId}/${locationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async submitStockCount(sessionId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions/${sessionId}/submit`, {
      method: 'POST',
    });
  }

  async approveStockCount(sessionId: string, approvalNotes?: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions/${sessionId}/approve${approvalNotes ? `?approval_notes=${encodeURIComponent(approvalNotes)}` : ''}`, {
      method: 'POST',
    });
  }

  async rejectStockCount(sessionId: string, reason: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions/${sessionId}/reject?reason=${encodeURIComponent(reason)}`, {
      method: 'POST',
    });
  }

  async getStockCountSummary(sessionId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/stock-count/sessions/${sessionId}/summary`);
  }

  // ==================== RECALLS ====================
  async getRecalls(params?: {
    status?: string;
    severity?: string;
    page?: number;
    page_size?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/recalls?${queryParams.toString()}`);
  }

  async getActiveRecalls(): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/recalls/active`);
  }

  async getRecall(recallId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/recalls/${recallId}`);
  }

  async createRecall(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/recalls`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRecall(recallId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/recalls/${recallId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getRecallItems(recallId: string, status?: string): Promise<any[]> {
    const url = status 
      ? `${API_BASE}/api/v1/recalls/${recallId}/items?status=${status}`
      : `${API_BASE}/api/v1/recalls/${recallId}/items`;
    return await fetchJSON(url);
  }

  async recordRecallAction(recallId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/recalls/${recallId}/action`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getRecallActions(recallId: string): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/recalls/${recallId}/actions`);
  }

  async getRecallStats(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/recalls/stats/summary`);
  }

  // ==================== CURRENCIES ====================
  async getCurrencies(activeOnly: boolean = true): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/currencies?active_only=${activeOnly}`);
  }

  async getActiveCurrencies(): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/currencies/active`);
  }

  async getExchangeRates(params?: {
    from_currency?: string;
    to_currency?: string;
    active_only?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.from_currency) queryParams.append('from_currency', params.from_currency);
    if (params?.to_currency) queryParams.append('to_currency', params.to_currency);
    if (params?.active_only !== undefined) queryParams.append('active_only', String(params.active_only));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/currencies/rates?${queryParams.toString()}`);
  }

  async createExchangeRate(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/currencies/rates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async convertCurrency(amount: number, fromCurrency: string, toCurrency: string, date?: string): Promise<any> {
    const queryParams = new URLSearchParams({
      amount: String(amount),
      from_currency: fromCurrency,
      to_currency: toCurrency,
    });
    if (date) queryParams.append('date', date);
    return await fetchJSON(`${API_BASE}/api/v1/currencies/convert?${queryParams.toString()}`);
  }

  async getLatestRate(fromCurrency: string, toCurrency: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/currencies/rate/latest?from_currency=${fromCurrency}&to_currency=${toCurrency}`);
  }

  // ==================== ASSEMBLY/BOM ====================
  async getBOMs(params?: {
    finished_item_id?: string;
    bom_type?: string;
    active_only?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.finished_item_id) queryParams.append('finished_item_id', params.finished_item_id);
    if (params?.bom_type) queryParams.append('bom_type', params.bom_type);
    if (params?.active_only !== undefined) queryParams.append('active_only', String(params.active_only));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/assembly/bom?${queryParams.toString()}`);
  }

  async getBOM(bomId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/bom/${bomId}`);
  }

  async getBOMComponents(bomId: string): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/bom/${bomId}/components`);
  }

  async createBOM(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/bom`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBOM(bomId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/bom/${bomId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getBOMCostBreakdown(bomId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/bom/${bomId}/cost-breakdown`);
  }

  async getProductionOrders(params?: {
    status?: string;
    bom_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.bom_id) queryParams.append('bom_id', params.bom_id);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    return await fetchJSON(`${API_BASE}/api/v1/assembly/production?${queryParams.toString()}`);
  }

  async getProductionOrder(productionId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/production/${productionId}`);
  }

  async createProductionOrder(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/production`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async startProduction(productionId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/production/${productionId}/start`, {
      method: 'POST',
    });
  }

  async completeProduction(productionId: string, quantityProduced: number, quantityScrapped?: number): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/production/${productionId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ 
        quantity_produced: quantityProduced,
        quantity_scrapped: quantityScrapped 
      }),
    });
  }

  async getProductionMaterials(productionId: string): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/production/${productionId}/materials`);
  }

  async issueMaterial(productionId: string, itemId: string, quantity: number, batchId?: string, notes?: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/assembly/production/${productionId}/issue-material`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId, quantity, batch_id: batchId, notes }),
    });
  }

  // ==================== QUALITY CONTROL ====================
  async getQCLots(params?: { status?: string; source_type?: string; vendor_id?: string }): Promise<any[]> {
    return this.get('/quality-control/lots', { params });
  }

  async getQCLot(lotId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/lots/${lotId}`);
  }

  async createQCLot(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/lots`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateQCLotStatus(lotId: string, status: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/lots/${lotId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async getQCChecklists(params?: { checklist_type?: string }): Promise<any[]> {
    return this.get('/quality-control/checklists', { params });
  }

  async getQCChecklist(checklistId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/checklists/${checklistId}`);
  }

  async createQCChecklist(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/checklists`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteQCChecklist(checklistId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/checklists/${checklistId}`, {
      method: 'DELETE',
    });
  }

  async getQuarantineItems(params?: { status?: string; reason?: string }): Promise<any[]> {
    return this.get('/quality-control/quarantine', { params });
  }

  async createQuarantineItem(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/quarantine`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateQuarantineItem(quarantineId: string, action: string, disposalMethod?: string, notes?: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/quarantine/${quarantineId}`, {
      method: 'PUT',
      body: JSON.stringify({ action, disposal_method: disposalMethod, notes }),
    });
  }

  async getQCStats(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/quality-control/stats`);
  }

  // ==================== MFA ====================
  async getMFASettings(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/settings`);
  }

  async setupMFA(mfaType: string, phone?: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/setup`, {
      method: 'POST',
      body: JSON.stringify({ mfa_type: mfaType, phone }),
    });
  }

  async verifyMFASetup(code: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/verify-setup`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async disableMFA(code: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/disable`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async createMFAChallenge(module: string, action: string, referenceId?: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/challenge`, {
      method: 'POST',
      body: JSON.stringify({ mfa_type: 'totp', module, action, reference_id: referenceId }),
    });
  }

  async verifyMFAChallenge(challengeId: string, code: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/verify?challenge_id=${challengeId}`, {
      method: 'POST',
      body: JSON.stringify({ challenge_id: challengeId, code }),
    });
  }

  async checkApprovalMFARequired(module: string, action: string, amount?: number): Promise<any> {
    return this.get('/mfa/check-approval-mfa', { params: { module, action, amount } });
  }

  async getApprovalMFARequirements(): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/approval-requirements`);
  }

  async createApprovalMFARequirement(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/approval-requirements`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMFAVerificationLog(): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/mfa/verification-log`);
  }

  // ==================== WEBHOOKS ====================
  async getWebhookEndpoints(isActive?: boolean): Promise<any[]> {
    const params = isActive !== undefined ? { is_active: isActive } : {};
    return this.get('/webhooks/endpoints', { params });
  }

  async createWebhookEndpoint(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/endpoints`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getWebhookEndpoint(endpointId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/endpoints/${endpointId}`);
  }

  async updateWebhookEndpoint(endpointId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/endpoints/${endpointId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteWebhookEndpoint(endpointId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/endpoints/${endpointId}`, {
      method: 'DELETE',
    });
  }

  async regenerateWebhookSecret(endpointId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/endpoints/${endpointId}/regenerate-secret`, {
      method: 'POST',
    });
  }

  async getWebhookEventTypes(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/event-types`);
  }

  async createWebhookSubscription(endpointId: string, eventType: string, filterConditions?: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/subscriptions`, {
      method: 'POST',
      body: JSON.stringify({ endpoint_id: endpointId, event_type: eventType, filter_conditions: filterConditions }),
    });
  }

  async deleteWebhookSubscription(subscriptionId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
    });
  }

  async getWebhookDeliveries(params?: any): Promise<any[]> {
    return this.get('/webhooks/deliveries', { params });
  }

  async retryWebhookDelivery(deliveryId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/webhooks/deliveries/${deliveryId}/retry`, {
      method: 'POST',
    });
  }

  async getWebhookStats(days?: number): Promise<any> {
    return this.get('/webhooks/stats', { params: { days } });
  }

  // ==================== DASHBOARD CUSTOMIZATION ====================
  async getDashboardLayouts(): Promise<any[]> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/layouts`);
  }

  async getDefaultDashboardLayout(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/layouts/default`);
  }

  async createDashboardLayout(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/layouts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getDashboardLayout(layoutId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/layouts/${layoutId}`);
  }

  async updateDashboardLayout(layoutId: string, data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/layouts/${layoutId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDashboardLayout(layoutId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/layouts/${layoutId}`, {
      method: 'DELETE',
    });
  }

  async duplicateDashboardLayout(layoutId: string, newName: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/layouts/${layoutId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ new_name: newName }),
    });
  }

  async resetDashboardToDefault(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/layouts/reset-default`, {
      method: 'POST',
    });
  }

  async getAvailableWidgets(params?: { category?: string; widget_type?: string }): Promise<any[]> {
    return this.get('/dashboard/widgets', { params });
  }

  async getWidgetCategories(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/widgets/categories`);
  }

  async getWidgetData(widgetKey: string, config?: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/dashboard/widget-data/${widgetKey}?config=${encodeURIComponent(JSON.stringify(config || {}))}`);
  }

  // ==================== DATA EXPORT/IMPORT ====================
  async getExportTypes(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/export-types`);
  }

  async getExportJobs(params?: { status?: string; job_type?: string }): Promise<any[]> {
    return this.get('/data/exports', { params });
  }

  async createExportJob(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/exports`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getExportJob(jobId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/exports/${jobId}`);
  }

  async deleteExportJob(jobId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/exports/${jobId}`, {
      method: 'DELETE',
    });
  }

  async getImportTypes(): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/import-types`);
  }

  async getImportJobs(params?: { status?: string; job_type?: string }): Promise<any[]> {
    return this.get('/data/imports', { params });
  }

  async createImportJob(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/imports`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadImportFile(jobId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    const accessToken = localStorage.getItem('access_token');
    const headers: Record<string, string> = {};
    if (accessToken && accessToken !== 'true') {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    const response = await fetch(`${API_BASE}/api/v1/data/imports/${jobId}/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    
    return response.json();
  }

  async executeImport(jobId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/imports/${jobId}/execute`, {
      method: 'POST',
    });
  }

  async getImportJob(jobId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/imports/${jobId}`);
  }

  async getImportStagingData(jobId: string, validationStatus?: string): Promise<any[]> {
    const params = validationStatus ? { validation_status: validationStatus } : {};
    return this.get(`/data/imports/${jobId}/staging`, { params });
  }

  async getImportErrors(jobId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/imports/${jobId}/errors`);
  }

  async deleteImportJob(jobId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/imports/${jobId}`, {
      method: 'DELETE',
    });
  }

  async getImportTemplates(templateType?: string): Promise<any[]> {
    const params = templateType ? { template_type: templateType } : {};
    return this.get('/data/templates', { params });
  }

  async createImportTemplate(data: any): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async downloadImportTemplate(templateId: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/templates/${templateId}/download`);
  }

  async suggestColumnMapping(sourceColumns: string[], targetType: string): Promise<any> {
    return await fetchJSON(`${API_BASE}/api/v1/data/mapping-suggest`, {
      method: 'POST',
      body: JSON.stringify({ source_columns: sourceColumns, target_type: targetType }),
    });
  }
}

export const api = new ApiService();
